import { PhoneIcon } from "@chakra-ui/icons";
import {
  Flex,
  FlexProps,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
} from "@chakra-ui/react";
import { FaAddressBook, FaRobot, FaUserCircle, FaUserNurse } from "react-icons/fa";
import { MdElderly, MdOutlineDialpad } from "react-icons/md";
import { Messages } from "../../../core/api";
import DialerAgencyMembersSearch from "../../../shared/components/DialerAgencyMembersSearch";
import DialerBotAgentForm from "../../../shared/components/DialerBotAgentForm";
import DialerCaregiversSearch from "../../../shared/components/DialerCaregiversSearch";
import DialerPatientsSearch from "../../../shared/components/DialerPatientsSearch";
import DialerPhonebookSearch from "../../../shared/components/DialerPhonebookSearch";
import Dialpad from "../../../shared/components/DialPad";
import { AgencyMemberId } from "../../../shared/schema/schema";
import { useDialerCaregiversSearchQuery } from "../../caregiver/hooks/useDialerCaregiversSearchQuery";
import { useSearchPatientsQuery } from "../../patient/hooks/useSearchPatientsQuery";

export type SearchEntity =
  | (NonNullable<ReturnType<typeof useDialerCaregiversSearchQuery>["data"]>[number] & {
      type: "Caregiver";
    })
  | (NonNullable<ReturnType<typeof useSearchPatientsQuery>["data"]>[number] & {
      type: "Patient";
    });

export interface DialerTabsContext {
  hidePatients: boolean;
  hideCaregivers: boolean;
  hidePhoneNumber: boolean;
  hideAgencyMembers: boolean;
  hidePhonebook: boolean;
  hideBotAgents: boolean;
}

interface Props {
  isCalling: boolean;
  context: DialerTabsContext;
  flexProps?: FlexProps;
  predefinedPhoneNumber?: string;
  onClickDial: (
    invitee: Messages["TelephonyUnResolvedInviteToCallInvitee"] & {
      phonenumber: string;
    }
  ) => void;
  onClickAgencyMember: (agencyMemberId: AgencyMemberId) => void;
}

const DialerTabs = (props: Props) => {
  const {
    context: {
      hideAgencyMembers,
      hideCaregivers,
      hidePatients,
      hidePhoneNumber,
      hidePhonebook,
      hideBotAgents,
    },
  } = props;

  const createHandleClickDial =
    (
      context: Exclude<
        Messages["TelephonyUnResolvedInviteToCallInvitee"]["type"],
        "AgencyMember" | "BotAgent"
      >
    ) =>
    (E164number: string) =>
      props.onClickDial({ type: context, phonenumber: E164number });

  return (
    <Tabs variant="soft-rounded">
      <Flex alignItems="start" gap={8}>
        <TabList
          alignItems="start"
          flexDirection="column"
          gap={4}
          justifyContent="start"
          textAlign="start"
        >
          {!hidePhoneNumber && (
            <Tooltip label="Phone number" placement="left">
              <Tab borderRadius="full" h={14} w={14}>
                <MdOutlineDialpad size="16" />
              </Tab>
            </Tooltip>
          )}
          {!hideAgencyMembers && (
            <Tooltip label="Agency members" placement="left">
              <Tab borderRadius="full" h={14} w={14}>
                <FaUserCircle size="16" />
              </Tab>
            </Tooltip>
          )}
          {!hidePatients && (
            <Tooltip label="Patients" placement="left">
              <Tab borderRadius="full" h={14} w={14}>
                <MdElderly size="16" />
              </Tab>
            </Tooltip>
          )}
          {!hideCaregivers && (
            <Tooltip label="Caregivers" placement="left">
              <Tab borderRadius="full" h={14} w={14}>
                <FaUserNurse size="16" />
              </Tab>
            </Tooltip>
          )}
          {!hidePhonebook && (
            <Tooltip label="Phonebook" placement="left">
              <Tab borderRadius="full" h={14} w={14}>
                <FaAddressBook size="16" />
              </Tab>
            </Tooltip>
          )}
          {!hideBotAgents && (
            <Tooltip label="Bot agents" placement="left">
              <Tab borderRadius="full" h={14} w={14}>
                <FaRobot size="16" />
              </Tab>
            </Tooltip>
          )}
        </TabList>
        <TabPanels flex={1}>
          {!hidePhoneNumber && (
            <TabPanel p="0">
              <Dialpad
                state={{
                  type: "PhoneNumberDialer",
                  dialButtonConent: "Call",
                  dialButtonLeftIcon: <PhoneIcon />,
                  isCalling: props.isCalling,
                  predefinedPhoneNumber: props.predefinedPhoneNumber,
                  onDialNumber: createHandleClickDial("NotIdentified"),
                }}
              />
            </TabPanel>
          )}
          {!hideAgencyMembers && (
            <TabPanel p="0">
              <DialerAgencyMembersSearch
                isLoading={props.isCalling}
                onClickAgencyMember={props.onClickAgencyMember}
              />
            </TabPanel>
          )}
          {!hidePatients && (
            <TabPanel
              as={Flex}
              flexDirection="column"
              gap={4}
              hidden={props.context.hidePatients}
              p="0"
              textAlign="start"
            >
              <DialerPatientsSearch
                isLoading={props.isCalling}
                onClickDial={createHandleClickDial("Patient")}
              />
            </TabPanel>
          )}
          {!hideCaregivers && (
            <TabPanel
              as={Flex}
              flexDirection="column"
              gap={4}
              hidden={props.context.hideCaregivers}
              p="0"
              textAlign="start"
            >
              <DialerCaregiversSearch
                isLoading={props.isCalling}
                onClickDial={createHandleClickDial("Caregiver")}
              />
            </TabPanel>
          )}
          {!hidePhonebook && (
            <TabPanel
              as={Flex}
              flexDirection="column"
              gap={4}
              hidden={props.context.hidePhonebook}
              p="0"
              textAlign="start"
            >
              <DialerPhonebookSearch
                isLoading={props.isCalling}
                onClickDial={createHandleClickDial("PhonebookContact")}
              />
            </TabPanel>
          )}
          {!hideBotAgents && (
            <TabPanel
              as={Flex}
              flexDirection="column"
              gap={4}
              hidden={props.context.hideBotAgents}
              p="0"
              textAlign="start"
            >
              <DialerBotAgentForm
                isDialing={props.isCalling}
                onDial={(botAgent) =>
                  props.onClickDial({
                    type: "BotAgent",
                    botAgent,
                    phonenumber: botAgent.phoneNumber,
                  })
                }
              />
            </TabPanel>
          )}
        </TabPanels>
      </Flex>
    </Tabs>
  );
};

export default DialerTabs;
